import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Star, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from "@/lib/slugUtils";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import FilloutButton from "@/components/FilloutButton";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbStructuredData from "@/components/BreadcrumbStructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import OptimizedImage from "@/components/OptimizedImage";

const About = () => {
  const { t } = useTranslation(['about', 'common']);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleContactClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };
  
  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="about" />
      <BreadcrumbStructuredData />
      <Header />
      
      {/* Breadcrumb Navigation */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4 text-center">
          <Badge variant="outline" className="mb-4">{t('about:badge')}</Badge>
          <h1 className="text-5xl font-bold mb-6">{t('about:title')}</h1>
          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
            {t('about:subtitle')}
          </p>
          <div className="flex justify-center">
            <OptimizedImage 
              src="/images/daniel.png"
              placeholder="https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=64&h=64&q=10"
              alt={t('about:imageAlt')}
              className="w-64 h-64 rounded-2xl object-cover object-center shadow-elegant hover:scale-105 transition-elegant"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>{t('about:intro.text')}</p>
              <div className="bg-accent/20 p-6 rounded-lg border-l-4 border-primary">
                <p className="font-semibold text-primary">{t('about:intro.highlight')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roots */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:roots.title')}</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>{t('about:roots.content')}</p>
              <p>{t('about:roots.learning')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:challenges.title')}</h2>
            <p className="text-lg text-muted-foreground mb-6">{t('about:challenges.intro')}</p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {(t('about:challenges.problems', { returnObjects: true }) as string[]).map((problem: string, index: number) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-accent/20 rounded-lg">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-muted-foreground">{problem}</p>
                </div>
              ))}
            </div>
            <p className="text-muted-foreground italic">{t('about:challenges.realization')}</p>
          </div>
        </div>
      </section>

      {/* First Step */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:firstStep.title')}</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>{t('about:firstStep.story')}</p>
              <div className="bg-green-500/10 p-6 rounded-lg border border-green-500/20">
                <h3 className="text-xl font-bold text-green-700 dark:text-green-400 mb-4">{t('about:firstStep.results.title')}</h3>
                <div className="grid md:grid-cols-1 gap-4">
                  {(t('about:firstStep.results.items', { returnObjects: true }) as string[]).map((result: string, index: number) => (
                    <div key={index} className="flex items-start gap-3">
                      <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-green-700 dark:text-green-300">{result}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-muted-foreground italic">{t('about:firstStep.insight')}</p>
            </div>
            
            {/* CTA after First Step */}
            <div className="text-center mt-8 pt-8 border-t border-border">
              <p className="text-lg mb-4 text-muted-foreground">
                {t('about:ctaMid1.text')}
              </p>
              <FilloutButton size="lg" className="group">
                {t('about:ctaMid1.button')}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </FilloutButton>
            </div>
          </div>
        </div>
      </section>

      {/* Birth of PriceForms */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:birthOfPriceForms.title')}</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>{t('about:birthOfPriceForms.intro')}</p>
              <div className="bg-accent/20 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Target companies:</h3>
                <ul className="space-y-2">
                  {(t('about:birthOfPriceForms.targetFirms', { returnObjects: true }) as string[]).map((firm: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-3 flex-shrink-0"></div>
                      <span>{firm}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p>{t('about:birthOfPriceForms.mission')}</p>
              <p className="font-semibold">{t('about:birthOfPriceForms.philosophy')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who I Help */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:whoIHelp.title')}</h2>
            <p className="text-lg text-muted-foreground mb-8">{t('about:whoIHelp.intro')}</p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {(t('about:whoIHelp.clientTypes', { returnObjects: true }) as string[]).map((type: string, index: number) => (
                <div key={index} className="bg-card p-4 rounded-lg border">
                  <p className="font-semibold">{type}</p>
                </div>
              ))}
            </div>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>{t('about:whoIHelp.customization')}</p>
              <p>{t('about:whoIHelp.approach')}</p>
            </div>
            
            {/* CTA after Who I Help */}
            <div className="text-center mt-8 pt-8 border-t border-border">
              <p className="text-lg mb-4 text-muted-foreground">
                {t('about:ctaMid2.text')}
              </p>
              <FilloutButton size="lg" variant="secondary" className="group">
                {t('about:ctaMid2.button')}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </FilloutButton>
            </div>
          </div>
        </div>
      </section>

      {/* How I Work */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:howIWork.title')}</h2>
            <p className="text-lg text-muted-foreground mb-6">{t('about:howIWork.intro')}</p>
            <div className="bg-accent/20 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-bold mb-4">My expertise:</h3>
              <ul className="space-y-2">
                {(t('about:howIWork.expertise', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-3 flex-shrink-0"></div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <h3 className="text-2xl font-bold mb-6">{t('about:howIWork.process.title')}</h3>
            <div className="space-y-6 mb-6">
              {(t('about:howIWork.process.steps', { returnObjects: true }) as string[]).map((step: string, index: number) => (
                <div key={index} className="flex gap-6">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-muted-foreground mt-2">{step}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-lg text-muted-foreground font-semibold">{t('about:howIWork.languages')}</p>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:technologies.title')}</h2>
            <div className="grid md:grid-cols-1 gap-4 mb-8">
              {(t('about:technologies.tools', { returnObjects: true }) as string[]).map((tool: string, index: number) => (
                <div key={index} className="bg-card p-4 rounded-lg border">
                  <span className="font-semibold">{tool}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-muted-foreground">{t('about:technologies.approach')}</p>
            
            {/* CTA after Technologies */}
            <div className="text-center mt-8 pt-8 border-t border-border">
              <p className="text-lg mb-4 text-muted-foreground">
                {t('about:ctaMid3.text')}
              </p>
              <Button size="lg" variant="outline" onClick={handleContactClick} className="group">
                {t('about:ctaMid3.button')}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Ecosystem Section - Compact Design */}
      <section className="py-20 bg-gradient-to-b from-background via-accent/5 to-background relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('about:digitalEcosystem.title')}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('about:digitalEcosystem.intro')}
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-10">
            {/* Bidmio Card */}
            <div className="group relative bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-2xl p-8 hover:shadow-xl hover:shadow-amber-500/10 transition-all duration-300">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Small Icon */}
                <div className="flex-shrink-0">
                  <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-amber-600 dark:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-2xl md:text-3xl font-bold">{t('about:digitalEcosystem.projects.bidmio.name')}</h3>
                    <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs">
                      {t('about:digitalEcosystem.projects.bidmio.market')}
                    </Badge>
                  </div>

                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t('about:digitalEcosystem.projects.bidmio.description')}
                  </p>

                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 inline-block">
                    <p className="text-sm">
                      <span className="font-semibold text-amber-700 dark:text-amber-400">{t('about:digitalEcosystem.targetAudience')}</span>{' '}
                      <span className="text-foreground/90">{t('about:digitalEcosystem.projects.bidmio.audience')}</span>
                    </p>
                  </div>

                  <div>
                    <Button 
                      asChild 
                      className="group/btn bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                    >
                      <a href={t('about:digitalEcosystem.projects.bidmio.url')} target="_blank" rel="noopener noreferrer" data-testid="button-visit-bidmio">
                        {t('about:digitalEcosystem.visitButton')} {t('about:digitalEcosystem.projects.bidmio.name')}
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* VibeCoder Card */}
            <div className="group relative bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border border-emerald-500/20 rounded-2xl p-8 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-300">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Small Icon */}
                <div className="flex-shrink-0">
                  <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-emerald-600 dark:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-2xl md:text-3xl font-bold">{t('about:digitalEcosystem.projects.vibecoder.name')}</h3>
                    <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-xs">
                      {t('about:digitalEcosystem.projects.vibecoder.market')}
                    </Badge>
                  </div>

                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t('about:digitalEcosystem.projects.vibecoder.description')}
                  </p>

                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 inline-block">
                    <p className="text-sm">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">{t('about:digitalEcosystem.targetAudience')}</span>{' '}
                      <span className="text-foreground/90">{t('about:digitalEcosystem.projects.vibecoder.audience')}</span>
                    </p>
                  </div>

                  <div>
                    <Button 
                      asChild 
                      className="group/btn bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                    >
                      <a href={t('about:digitalEcosystem.projects.vibecoder.url')} target="_blank" rel="noopener noreferrer" data-testid="button-visit-vibecoder">
                        {t('about:digitalEcosystem.visitButton')} {t('about:digitalEcosystem.projects.vibecoder.name')}
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Message */}
          <div className="mt-16 text-center">
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('about:digitalEcosystem.closingMessage')}
            </p>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">{t('about:experience.title')}</h2>
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>{t('about:experience.stats')}</p>
              <div>
                <p className="mb-2">{t('about:experience.countries.intro')}</p>
                <div className="flex gap-4 flex-wrap">
                  {(t('about:experience.countries.list', { returnObjects: true }) as string[]).map((country: string, index: number) => (
                    <Badge key={index} variant="secondary">{country}</Badge>
                  ))}
                </div>
              </div>
              <p>{t('about:experience.understanding')}</p>
              <p className="font-semibold">{t('about:experience.quality')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{t('about:cta.title')}</h2>
            <p className="text-xl mb-8 opacity-90">
              {t('about:cta.intro')}
            </p>
            
            {/* Benefits List */}
            <div className="grid md:grid-cols-2 gap-4 mb-8 text-left">
              {(t('about:cta.benefits', { returnObjects: true }) as string[]).map((benefit: string, index: number) => (
                <div key={index} className="flex items-start gap-3 bg-white/10 p-4 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="opacity-90">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="mb-8">
              <p className="text-lg mb-6 opacity-90">{t('about:cta.invitation')}</p>
              <FilloutButton size="lg" variant="secondary">
                {t('about:cta.button')}
              </FilloutButton>
            </div>
            
            {/* Closing Signature */}
            <div className="text-left max-w-md mx-auto mt-12 pt-8 border-t border-white/20">
              <p className="mb-2 opacity-90">{t('about:cta.closing')}</p>
              <p className="font-bold text-lg">{t('about:cta.name')}</p>
              <p className="text-sm opacity-75">{t('about:cta.title')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;